import React, { useEffect, useState, useRef } from 'react';
import { AdminPointPolicyDto, AdminPointPolicyRequestDto } from 'services/data-contracts';
import styled from 'styled-components';

const INPUT_PADDING_VERTICAL = 8;
const INPUT_PADDING_HORIZONTAL = 12;
const INPUT_HEIGHT = 40;

// 포인트 정책 표시용 타입 정의
interface PointPolicyItemDto {
  id: keyof Omit<AdminPointPolicyRequestDto, 'expireYears'>;
  name: string;
  description: string;
  points: number;
}

const StyledInputField = styled(InputField)`
  .MuiOutlinedInput-input {
    padding: ${INPUT_PADDING_VERTICAL}px ${INPUT_PADDING_HORIZONTAL}px !important;
  }

  .MuiInputBase-input {
    padding: ${INPUT_PADDING_VERTICAL}px ${INPUT_PADDING_HORIZONTAL}px !important;
  }

  .MuiInputBase-root {
    height: ${INPUT_HEIGHT}px;
  }
`;

// 개별 포인트 정책 아이템 컴포넌트
const PointPolicyItem = ({
  pointPolicy,
  onUpdate,
  isChanged = false,
}: {
  pointPolicy: PointPolicyItemDto;
  onUpdate: (id: keyof Omit<AdminPointPolicyRequestDto, 'expireYears'>, value: number) => void;
  isChanged?: boolean;
}) => {
  const [localValue, setLocalValue] = useState(pointPolicy.points?.toString() || '');

  const handleChange = (value: string) => {
    setLocalValue(value);
  };

  const handleBlur = () => {
    const numValue = Number(localValue);
    if (!isNaN(numValue) && numValue >= 0) {
      onUpdate(pointPolicy.id, numValue);
    }
  };

  useEffect(() => {
    setLocalValue(pointPolicy.points?.toString() || '');
  }, [pointPolicy.points]);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-start',
        gap: 20,
        alignItems: 'center',
        padding: '15px',
        borderRadius: '8px',
        backgroundColor: isChanged ? '#fff3cd' : 'transparent',
        border: isChanged ? '1px solid #ffeaa7' : '1px solid transparent',
        marginBottom: '15px',
      }}
    >
      <div style={{ minWidth: 200 }}>
        <div
          style={{ fontWeight: 600, fontSize: 18, color: 'var(--point-color)', marginBottom: 5 }}
        >
          {pointPolicy.name}
        </div>
        <div style={{ fontSize: 14, color: '#666' }}>{pointPolicy.description}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <StyledInputField
          onChange={handleChange}
          onBlur={handleBlur}
          type="number"
          style={{ width: 150 }}
          value={localValue}
          placeholder="0"
        />
        <div style={{ fontSize: 16, fontWeight: 500 }}>포인트</div>
      </div>
      {isChanged && (
        <div
          style={{
            fontSize: '12px',
            color: '#d68910',
            fontWeight: 'bold',
          }}
        >
          변경됨
        </div>
      )}
    </div>
  );
};

import { useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';
import ServiceHelper from 'stores/ServiceHelper';
import PageLayout from 'components/layouts/PageLayout';
import { InnerGroup, InnerSection } from 'components/layouts/CommonForm';
import { InputField, Row } from 'saladsoft-primitive';
import Button from 'components/input/Button';
import ButtonWrapper from '../listings/ButtonWrapper';
import Text from 'components/commons/Text';

const MemberPointPage = observer(() => {
  const location = useLocation();
  const [pointPolicies, setPointPolicies] = useState<PointPolicyItemDto[]>([]);
  const [changedCount, setChangedCount] = useState(0);
  const [apiData, setApiData] = useState<AdminPointPolicyDto | null>(null);
  const originalValues = useRef<AdminPointPolicyRequestDto | null>(null);
  const changedValues = useRef<Partial<AdminPointPolicyRequestDto>>({});

  const getPolicyDisplayData = (data: AdminPointPolicyDto | null): PointPolicyItemDto[] => [
    {
      id: 'join',
      name: '회원가입',
      description: '신규 회원가입 시 지급되는 포인트',
      points: data?.join || 0,
    },
    {
      id: 'birthday',
      name: '생일',
      description: '생일 당월에 지급되는 포인트',
      points: data?.birthday || 0,
    },
    {
      id: 'referral',
      name: '추천인',
      description: '친구 추천 시 지급되는 포인트',
      points: data?.referral || 0,
    },
  ];

  const fetchPointPolicies = async () => {
    try {
      const response = await ServiceHelper.adminPointPolicyService?.getPointPolicy();
      if (response?.data) {
        setApiData(response.data);
        const displayData = getPolicyDisplayData(response.data);
        setPointPolicies(displayData);

        // 원본 값 저장
        originalValues.current = {
          join: response.data.join || 0,
          birthday: response.data.birthday || 0,
          referral: response.data.referral || 0,
          expireYears: response.data.expireYears || 1,
        };

        changedValues.current = {};
        setChangedCount(0);
      }
    } catch (error) {
      console.error('포인트 정책 조회 실패:', error);
      // 기본값으로 초기화
      const displayData = getPolicyDisplayData(null);
      setPointPolicies(displayData);
      originalValues.current = {
        join: 0,
        birthday: 0,
        referral: 0,
        expireYears: 1,
      };
    }
  };

  const handleUpdatePointPolicy = (
    id: keyof Omit<AdminPointPolicyRequestDto, 'expireYears'>,
    value: number,
  ) => {
    if (!originalValues.current) return;

    // 원본 값과 비교
    const originalValue = originalValues.current[id];

    if (originalValue !== value) {
      // 변경된 경우 changedValues에 추가
      changedValues.current[id] = value;
    } else {
      // 원본과 같은 값으로 되돌아간 경우 changedValues에서 제거
      delete changedValues.current[id];
    }

    // UI 업데이트를 위해 현재 상태 재계산
    const updatedPolicies = pointPolicies.map((policy) => {
      if (policy.id === id) {
        return { ...policy, points: value };
      }
      return policy;
    });

    setPointPolicies(updatedPolicies);
    setChangedCount(Object.keys(changedValues.current).length);
  };

  const handleSavePointPolicies = async () => {
    if (!originalValues.current || Object.keys(changedValues.current).length === 0) {
      console.log('변경된 정책이 없습니다.');
      return;
    }

    console.log('변경된 포인트 정책들:', changedValues.current);

    try {
      // 전체 요청 데이터 구성
      const requestData: AdminPointPolicyRequestDto = {
        join: changedValues.current.join ?? originalValues.current.join,
        birthday: changedValues.current.birthday ?? originalValues.current.birthday,
        referral: changedValues.current.referral ?? originalValues.current.referral,
        expireYears: changedValues.current.expireYears ?? originalValues.current.expireYears,
      };

      // API 호출
      const response = await ServiceHelper.adminPointPolicyService?.savePointPolicy(requestData);

      if (response?.status === 200) {
        // 상태 초기화
        changedValues.current = {};
        setChangedCount(0);

        // 최신 데이터 다시 가져오기
        await fetchPointPolicies();

        console.log('모든 포인트 정책이 성공적으로 저장되었습니다.');
        alert('포인트 정책이 저장되었습니다.');
      } else {
        throw new Error('저장 실패');
      }
    } catch (error) {
      console.error('포인트 정책 저장 중 오류가 발생했습니다:', error);
      alert('저장 중 오류가 발생했습니다.');
    }
  };

  useEffect(() => {
    fetchPointPolicies();
  }, [location.search]);

  return (
    <PageLayout title="포인트 정책">
      <InnerSection>
        <InnerGroup>
          <div>
            <Text type="H3">포인트 정책</Text>
            <Text type="H4">포인트 정책을 설정해주세요.</Text>
            {changedCount > 0 && (
              <div
                style={{
                  marginTop: '10px',
                  padding: '8px 12px',
                  backgroundColor: '#e8f5e8',
                  border: '1px solid #28a745',
                  borderRadius: '4px',
                  fontSize: '14px',
                  color: '#155724',
                }}
              >
                변경된 정책: {changedCount}개
              </div>
            )}
          </div>
          <div>
            {pointPolicies.map((pointPolicy) => (
              <PointPolicyItem
                key={pointPolicy.id}
                pointPolicy={pointPolicy}
                onUpdate={handleUpdatePointPolicy}
                isChanged={pointPolicy.id in changedValues.current}
              />
            ))}
          </div>
        </InnerGroup>
      </InnerSection>
      <ButtonWrapper>
        <Button
          onClick={handleSavePointPolicies}
          isValid={changedCount > 0}
          style={{
            opacity: changedCount === 0 ? 0.5 : 1,
            cursor: changedCount === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          저장 {changedCount > 0 && `(${changedCount}개 변경)`}
        </Button>
      </ButtonWrapper>
    </PageLayout>
  );
});

export default MemberPointPage;
